import React from "react";
import { FiSettings } from "react-icons/fi";
import { useLocation } from "@reach/router";
import { BaseMemberPaths } from "newui/utils/navigation/navigation.types";
import { useCurrentUserHasFeatureFlag, useHasRfxSettings } from "hooks/featureFlags";
import { CurrentAccountType, useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { AppMenuItem } from "./AppMenuItem/AppMenuItem";
import { ExpandableMenuItem, ExpandableMenuChildItem } from "./ExpandableMenuItem";
import { useIsCurrentUserAdmin } from "hooks/auth";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";

interface SettingsExpandableMenuProps {
	isCollapsed: boolean;
	paths: BaseMemberPaths;
	onNavigate?: () => void;
}

const STORAGE_KEY = "settingsMenu_expanded";

export const SettingsExpandableMenu: React.FC<SettingsExpandableMenuProps> = ({
	isCollapsed,
	paths,
	onNavigate,
}) => {
	const isAdminUser = useIsCurrentUserAdmin();
	const hasRfxSettings = useHasRfxSettings();
	const profileContext = useProfileContext();
	const isSupplierReportBrandingEnabled = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_REPORT_BRANDING_FOR_SUPPLIER);
	const isProcurer = profileContext?.currentProfile === CurrentAccountType.PROCURER;
	const location = useLocation();
	const currentPath = location.pathname;

	const accountSettingsLink = paths.accountSettings.userManagement;
	const usersLink = paths.accountSettings.users.list;
	const rfxSettingsLink = paths.rfxSettings;
	const pdfSettingsLink = paths.pdfSettings;

	const isAccountSettingsActive = currentPath.includes("account-settings") && !currentPath.includes("account-settings/users");
	const isUsersActive = currentPath.includes("account-settings/users");
	const isRfxSettingsActive = currentPath.includes("rfx-settings");
	const isPdfSettingsActive = currentPath.includes("pdf-settings");

	const showRfxSettings = (hasRfxSettings && isProcurer) || !isProcurer;
	const hasChildren = showRfxSettings || isProcurer || isAdminUser;

	if (!isAdminUser) {
		return null;
	}

	if (!hasChildren) {
		return (
			<AppMenuItem
				name="Settings"
				icon={<FiSettings size={24} />}
				link={accountSettingsLink}
				isCollapsed={isCollapsed}
				testId="account-settings"
				tourName="AccountSettings"
				onNavigate={onNavigate}
			/>
		);
	}

	return (
		<ExpandableMenuItem
			icon={<FiSettings size={24} />}
			label="Settings"
			storageKey={STORAGE_KEY}
			isCollapsed={isCollapsed}
			collapsedLink={accountSettingsLink}
			testId="settings-menu"
			onNavigate={onNavigate}
		>
			<ExpandableMenuChildItem
				name="Account Settings"
				link={accountSettingsLink}
				isActive={isAccountSettingsActive}
				testId="account-settings-child"
				tourName="AccountSettings"
				onNavigate={onNavigate}
			/>
			<ExpandableMenuChildItem
				name="User Management"
				link={usersLink}
				isActive={isUsersActive}
				testId="users-child"
				onNavigate={onNavigate}
				tourName="UserManagement"
			/>
			{showRfxSettings && (
				<ExpandableMenuChildItem
					name="RFx Settings"
					link={rfxSettingsLink}
					isActive={isRfxSettingsActive}
					testId="rfx-settings-child"
					onNavigate={onNavigate}
				/>
			)}
			{(isSupplierReportBrandingEnabled) && (
				<ExpandableMenuChildItem
					name="PDF Report Settings"
					link={pdfSettingsLink}
					isActive={isPdfSettingsActive}
					testId="pdf-settings-child"
					onNavigate={onNavigate}
				/>
			)}
		</ExpandableMenuItem>
	);
};
